import { ArrowExpand01Icon } from "hugeicons-react";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Resource } from "../../../types/resource";
import "../../../Styles/Layout/Items/quizCard.css";
import { useResourceContext } from "../../../contexts/ResourceContext";
import { getResourceIcon } from "../../../Data/IconData";
import { useQuizSession, QuizSession } from "../../../hooks/useQuizSession";
import { QuizQuestion } from "../../../helpers/quizHelper";

const MAX_DOTS = 12;

/**
 * Quiz card in a message. The quiz can be taken right here, one question at
 * a time; expand opens the side panel on the same quiz.
 */
export const QuizItem = ({ resource, messageId, quizId }: { resource: Resource, messageId: string, quizId?: string }) => {
    const { handleResourceClick, currentChatResources } = useResourceContext();

    // Older messages only name the quiz; match the newest quiz with that title.
    const resolvedQuizId = quizId ?? [...currentChatResources]
        .reverse()
        .find(r => r.type === 'quiz' && r.title === resource.title)?.id;

    const session = useQuizSession(resolvedQuizId);
    const { quiz, isGraded, currentQuestion } = session;
    const title = quiz?.title || resource.title || 'Quiz';
    const isReady = !!quiz && session.questions.length > 0;

    const expand = () => handleResourceClick(resource.title, messageId, resolvedQuizId);

    return (
        <div className="quiz-card">
            <div className="resource-preview-header quiz-card-header">
                <div className="resource-preview-header-left">
                    {getResourceIcon('quiz')}
                    <h1>{title}</h1>
                </div>
                <div className="resource-preview-header-right">
                    {session.isLoading || !resolvedQuizId ? (
                        <div className="resource-loading-spinner" />
                    ) : isReady ? (
                        <button type="button" onClick={expand} aria-label="Open quiz in panel">
                            <ArrowExpand01Icon size={15} />
                        </button>
                    ) : null}
                </div>
            </div>

            {!session.isLoading && resolvedQuizId && !isReady && (
                <p className="quiz-card-status">This quiz isn't available.</p>
            )}

            {isReady && !isGraded && currentQuestion && (
                <div className="quiz-card-body" key={currentQuestion.id}>
                    <QuestionView session={session} question={currentQuestion} />
                    <Footer session={session} question={currentQuestion} />
                </div>
            )}

            {isReady && isGraded && (
                <div className="quiz-card-body">
                    <Results session={session} />
                    <div className="quiz-card-pills">
                        <button type="button" className="quiz-card-pill" onClick={expand}>Review Answers</button>
                        <button type="button" className="quiz-card-pill" onClick={() => void session.retake()}>Retake</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const QuestionView = ({ session, question }: { session: QuizSession, question: QuizQuestion }) => {
    const locked = session.isLocked(question);
    const selected = session.selections[question.id];

    return (
        <>
            <p className="quiz-card-question">{question.text}</p>
            <div className="quiz-card-choices" role="radiogroup" aria-label={question.text}>
                {question.choices.map((choice, index) => {
                    const state = !locked
                        ? (selected === index ? 'selected' : 'idle')
                        : index === question.correctIndex
                            ? 'correct'
                            : selected === index ? 'incorrect' : 'dimmed';
                    return (
                        <button
                            key={index}
                            type="button"
                            role="radio"
                            aria-checked={selected === index}
                            className="quiz-card-choice"
                            data-state={state}
                            disabled={locked}
                            onClick={() => session.select(question, index)}
                        >
                            <span>{choice}</span>
                            {state === 'correct' && <Check size={16} aria-label="Correct answer" />}
                            {state === 'incorrect' && <X size={16} aria-label="Your answer, incorrect" />}
                        </button>
                    );
                })}
            </div>
            {locked && question.explanation && (
                <p className="quiz-card-explanation">{question.explanation}</p>
            )}
        </>
    );
};

const Footer = ({ session, question }: { session: QuizSession, question: QuizQuestion }) => {
    const locked = session.isLocked(question);
    const total = session.questions.length;

    return (
        <div className="quiz-card-footer">
            {total > MAX_DOTS ? (
                <span className="quiz-card-counter">{session.currentIndex + 1}/{total}</span>
            ) : (
                <div className="quiz-card-dots" aria-label={`Question ${session.currentIndex + 1} of ${total}`}>
                    {session.questions.map((q, index) => {
                        const answered = session.selections[q.id] !== undefined || !!session.revealed[q.id];
                        return (
                            <span
                                key={q.id}
                                className="quiz-card-dot"
                                data-current={index === session.currentIndex}
                                data-answered={answered}
                            />
                        );
                    })}
                </div>
            )}

            <div className="quiz-card-actions">
                {!locked && (
                    <button type="button" className="quiz-card-link" onClick={() => session.reveal(question)}>
                        Show Answer
                    </button>
                )}
                <button
                    type="button"
                    className="quiz-card-nav"
                    onClick={session.goToPrevious}
                    disabled={session.currentIndex === 0}
                    aria-label="Previous question"
                >
                    <ChevronLeft size={16} />
                </button>
                {session.isOnLastQuestion ? (
                    <button
                        type="button"
                        className="quiz-card-grade"
                        onClick={() => void session.submit()}
                        disabled={!session.canSubmit}
                        title={session.canSubmit ? undefined : 'Answer every question to grade'}
                    >
                        {session.isSubmitting ? 'Grading...' : 'Grade'}
                    </button>
                ) : (
                    <button type="button" className="quiz-card-nav" onClick={session.goToNext} aria-label="Next question">
                        <ChevronRight size={16} />
                    </button>
                )}
            </div>
        </div>
    );
};

const Results = ({ session }: { session: QuizSession }) => {
    const total = session.questions.length;
    const { score } = session;
    const missed = total - score;
    const ratio = total > 0 ? score / total : 0;
    const verdict = score === total
        ? 'All correct'
        : `${ratio >= 0.7 ? 'Nice work' : ratio >= 0.4 ? 'Getting there' : 'Worth another go'} · ${missed} missed`;

    return (
        <div className="quiz-card-results">
            <div className="quiz-card-score">
                <span className="quiz-card-score-value">{score}/{total}</span>
                <span className="quiz-card-verdict" data-perfect={score === total}>{verdict}</span>
            </div>
            <div className="quiz-card-dots" aria-hidden>
                {session.questions.map(q => (
                    <span
                        key={q.id}
                        className="quiz-card-dot"
                        data-result={session.selections[q.id] === q.correctIndex ? 'correct' : 'incorrect'}
                    />
                ))}
            </div>
        </div>
    );
};
